import { articleDescriptionMaxLength } from '@texas/resources/constants';
import {
  Button,
  Flex,
  FormControl,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
} from '@chakra-ui/react';
import { articlesApi } from '@texas/api/endpoints/articlesApi';
import { customersApi } from '@texas/api/endpoints/customersApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { ReactSelectOption } from '@texas/types';
import { request } from '@texas/utils/helpers/httpHelpers';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TexasFormLabel } from '../shared/form/TexasFormLabel';
import { TexasSelect } from '../shared/form/TexasSelect';
import { useItemCodeValidator } from './useItemCodeValidator';
import { PreviewPane } from './PreviewPanel';
import { reactEvents } from '@bridge/reactEvents';

interface AddVdcArticleFormValues {
  customerId: number | null;
  conceptId: number | null;
  itemCode: string;
  description: string;
}

interface AddVdcArticleModalProps {
  isOpen: boolean;
  onClose: () => void;
  vdcPricingData: {
    productGroupId: number | null;
    branchIds: number[] | null;
    width: number;
    length: number;
    tierRangeId: number | null;
    compositionGroupId: number | null;
    treatmentIds: number[] | null;
  };
}

export function AddVdcArticleModal({
  isOpen,
  onClose,
  vdcPricingData,
}: AddVdcArticleModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const { request: addVdcRequest, loading: addingVdc } = useApiRequest(
    articlesApi.addVdcArticle,
  );

  const [customerSearchInput, setCustomerSearchInput] = useState('');

  const { register, handleSubmit, control, watch, reset, formState } =
    useForm<AddVdcArticleFormValues>({
      defaultValues: {
        customerId: null,
        conceptId: null,
        itemCode: '',
        description: '',
      },
    });

  const {
    data: customers,
    refetch: refetchCustomers,
    loading: loadingCustomers,
  } = useApiResource(customersApi.filterCustomers);

  const selectedCustomerId = watch('customerId');
  const itemCode = watch('itemCode');

  const { result, component, validateItemCode } =
    useItemCodeValidator(itemCode);

  const {
    data: concepts,
    refetch: refetchConcepts,
    loading: loadingConcepts,
  } = useApiResource(customersApi.getConcepts);

  useEffect(() => {
    if (customerSearchInput.length >= 2) {
      refetchCustomers({
        assignedTo: null,
        customerName: customerSearchInput,
        limit: 20,
      });
    }
  }, [customerSearchInput, refetchCustomers]);

  useEffect(() => {
    if (selectedCustomerId) {
      refetchConcepts(selectedCustomerId);
    }
  }, [selectedCustomerId, refetchConcepts]);

  const customerOptions = useMemo(() => {
    if (customerSearchInput.length < 2) {
      return [];
    }
    return (
      customers?.map<ReactSelectOption>((customer) => ({
        value: customer.id,
        label: customer.name,
      })) ?? []
    );
  }, [customers, customerSearchInput]);

  const conceptOptions = useMemo(() => {
    return (
      concepts?.map<ReactSelectOption>((concept) => ({
        value: concept.id,
        label: concept.name,
      })) ?? []
    );
  }, [concepts]);

  const onSubmit = async (data: AddVdcArticleFormValues) => {
    if (
      !data.customerId ||
      !vdcPricingData.productGroupId ||
      !vdcPricingData.compositionGroupId ||
      !vdcPricingData.branchIds ||
      !vdcPricingData.tierRangeId
    ) {
      toast({
        title: t('vdc.missingRequiredData'),
        description: t('vdc.ensureAllFieldsFilled'),
        status: 'error',
        isClosable: true,
      });
      return;
    }

    await request(
      addVdcRequest,
      [
        {
          itemCode: data.itemCode,
          description: data.description,
          productGroupId: vdcPricingData.productGroupId,
          templateCompositionGroupId: vdcPricingData.compositionGroupId,
          customerId: data.customerId,
          conceptId: data.conceptId,
          branchIds: vdcPricingData.branchIds,
          contactId: null,
          tierRangeId: vdcPricingData.tierRangeId,
          width: vdcPricingData.width,
          length: vdcPricingData.length,
          treatmentIds: vdcPricingData.treatmentIds,
        },
      ],
      (d) => {
        reactEvents.routingChanged.dispatch({
          to: 'article',
          navObj: { articleId: d.articleId },
        });
        toast({
          title: t('vdc.articleCreatedSuccessfully'),
          status: 'success',
          isClosable: true,
        });
        handleClose();
      },
      (error) => {
        toast({
          title: t('vdc.failedToCreateArticle'),
          description: error.message,
          status: 'error',
          isClosable: true,
        });
      },
    );
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      closeOnOverlayClick={false}
    >
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('vdc.addArticle')}</ModalHeader>
        <ModalCloseButton />
        <form onSubmit={handleSubmit(onSubmit)}>
          <ModalBody>
            <Flex flexDir="column" gap={4}>
              <PreviewPane
                formValues={vdcPricingData}
                selectedBranches={vdcPricingData.branchIds ?? []}
                disabled={true}
              />

              <FormControl isRequired={true}>
                <TexasFormLabel>{t('general.customer')}</TexasFormLabel>
                <Controller
                  name="customerId"
                  control={control}
                  rules={{ required: true }}
                  render={({ field }) => (
                    <TexasSelect
                      {...field}
                      isLoading={loadingCustomers}
                      isClearable={true}
                      placeholder={t('concept.searchBrand')}
                      value={
                        field.value
                          ? {
                              label:
                                customers?.find((c) => c.id === field.value)
                                  ?.name ?? '',
                              value: field.value,
                            }
                          : null
                      }
                      onChange={(e) => field.onChange(e?.value ?? null)}
                      onInputChange={(e) => setCustomerSearchInput(e)}
                      options={customerOptions}
                    />
                  )}
                />
              </FormControl>

              <FormControl>
                <TexasFormLabel>{t('general.concept')}</TexasFormLabel>
                <Controller
                  name="conceptId"
                  control={control}
                  render={({ field }) => (
                    <TexasSelect
                      {...field}
                      isLoading={loadingConcepts}
                      isClearable={true}
                      isDisabled={!selectedCustomerId}
                      value={
                        field.value
                          ? {
                              label:
                                concepts?.find((c) => c.id === field.value)
                                  ?.name ?? '',
                              value: field.value,
                            }
                          : null
                      }
                      onChange={(e) => field.onChange(e?.value ?? null)}
                      options={conceptOptions}
                      placeholder={
                        !selectedCustomerId
                          ? t('vdc.selectCustomerFirst')
                          : t('vdc.selectConcept')
                      }
                    />
                  )}
                />
              </FormControl>

              <FormControl
                isRequired={true}
                isInvalid={!result.isValid && !!itemCode}
              >
                <TexasFormLabel>{t('vdc.itemCode')}</TexasFormLabel>
                <Input
                  {...register('itemCode', { required: true })}
                  variant="texas"
                  onBlur={validateItemCode}
                />
                {component}
              </FormControl>

              <FormControl isRequired={true}>
                <TexasFormLabel>{t('vdc.description')}</TexasFormLabel>
                <Input
                  {...register('description', {
                    required: true,
                    maxLength: articleDescriptionMaxLength,
                  })}
                  maxLength={articleDescriptionMaxLength}
                  variant="texas"
                />
              </FormControl>
            </Flex>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={handleClose}>
              {t('general.cancel')}
            </Button>
            <Button
              type="submit"
              variant="texas-solid"
              isLoading={addingVdc}
              isDisabled={!result.isValid || !formState.isValid}
            >
              {t('vdc.createArticle')}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  );
}
